import { getCookieValue, AB_TEST_COOKIE } from 'utils/cookieStorage';

const MOBILE_BANNER_AB_TEST_NAME = 'mobileBanner';
const PROTOTYPE_AB_TEST_NAME = 'prototype';
const MESSENGER_AB_TEST_NAME = 'messenger';

/**
 * Při zapnutí nového testu je potřeba upravit názvy variant napr. 'zboziInListing/A'.
 * Pokud chceme, aby se spárovala data i ve Zbozi.cz analytice, je nutné kontaktovat backend kvůli aktualizaci
 * whitelist pravidel.
 *
 * Example usage:
 * AB_TEST_BUCKETS = [{
 * 		name: 'searchLayout/A',
 * 		min: 0,
 * 		max: 0.1,
 * 	},
 * 	{
 * 		name: 'searchLayout/B',
 * 		min: 0.1,
 * 		max: 0.2,
 * 	},
 * 	{
 * 		name: '', // rest of users will have empty name part, to prevent repeatedly distribution
 * 		min: 0.2,
 * 		max: 1,
 *	},
 * 	{
 * 		name: 'prototype', // min and max are optional - when not provided, the bucket is not part of random distribution
 * 	}]
 */
const AB_TEST_BUCKETS: { name: string; min?: number; max?: number }[] = [
	{
		name: `${MOBILE_BANNER_AB_TEST_NAME}/A`,
		min: 0,
		max: 0.5,
	},
	{
		name: `${MOBILE_BANNER_AB_TEST_NAME}/B`,
		min: 0.5,
		max: 1,
	},
	{
		name: PROTOTYPE_AB_TEST_NAME,
	},
	{
		name: MESSENGER_AB_TEST_NAME,
	},
];

export function getABTestVariant(cookies: string): string[] {
	const cookieValue = getCookieValue(cookies, AB_TEST_COOKIE);
	if (!cookieValue) {
		return [];
	}
	return cookieValue.split(',').map((variant) => variant.trim());
}

export function isMobileBannerABTestActive(cookies: string) {
	const abtestCookieValues = getABTestVariant(cookies);
	return abtestCookieValues.includes('mobileBanner/A');
}

export function isPrototypeActive(cookies: string, name?: string) {
	const abtestCookieValues = getABTestVariant(cookies);

	if (name) {
		return abtestCookieValues.includes(`${PROTOTYPE_AB_TEST_NAME}-${name}`);
	}

	return abtestCookieValues.includes(PROTOTYPE_AB_TEST_NAME);
}

export function isMessengerABTestActive(cookies: string) {
	const abtestCookieValues = getABTestVariant(cookies);
	return abtestCookieValues.includes(MESSENGER_AB_TEST_NAME);
}

const COOKIE_EXPIRATION = 7 * 24 * 60 * 60 * 1000; // ms
const URL_PARAM_COOKIE_EXPIRATION = 24 * 60 * 60 * 1000;

// Once we want to enable the distribution of the test, we need to set this to true
const DISTRIBUTION_ENABLED = true;

/**
 * Sets AB tests from URL parameter with 24-hour expiration
 * @param abtestset Comma-separated list of AB tests
 * @param setCookie Function to set cookie
 * @returns The AB test cookie value or null
 */
export function setAbtestsFromUrl(
	abtestset: string,
	setCookie: (name: string, value: string, options: { expires: Date }) => void,
) {
	const abtests = abtestset
		.split(',')
		.map((test) => test.trim())
		.filter(Boolean);

	if (abtests.length === 0) {
		return null;
	}

	const cookieValue = abtests.join(',');
	setCookie(AB_TEST_COOKIE, cookieValue, { expires: new Date(Date.now() + URL_PARAM_COOKIE_EXPIRATION) });

	return cookieValue;
}

export function initAbtest(
	abtestCookieValue: undefined | string,
	setCookie: (name: string, value: string, options: { expires: Date }) => void,
) {
	if (DISTRIBUTION_ENABLED && (abtestCookieValue === undefined || !isValidVariant(abtestCookieValue))) {
		return setNewVariant(setCookie);
	}

	return abtestCookieValue && isValidVariant(abtestCookieValue) ? abtestCookieValue : null;
}

function isValidVariant(abtestCookieValue: string) {
	const variants = abtestCookieValue.split(',').map((variant) => variant.trim());
	return variants.some((variant) => !!AB_TEST_BUCKETS.find(({ name }) => name === variant));
}

function setNewVariant(setCookie: (name: string, value: string, options: { expires: Date }) => void) {
	const random = Math.random();
	const variant = AB_TEST_BUCKETS.find(({ min, max }) => {
		if (min === undefined || max === undefined) {
			return false;
		}
		return min <= random && random < max;
	});

	if (variant) {
		setCookie(AB_TEST_COOKIE, variant.name, { expires: new Date(Date.now() + COOKIE_EXPIRATION) });
		return variant.name;
	}

	return null;
}
